import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Clock, Timer, FileText, AlertTriangle, 
  Stethoscope, TrendingUp, Calendar, Loader2, Plus, 
  LayoutDashboard, PieChart, BarChart3
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Importar componente de gráficos
import DashboardGraficosContent from './DashboardGraficosContent';

const EmpresaDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Tab ativa baseada na URL
  const activeTab = searchParams.get('tab') || 'resumo';

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard/empresa');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const stats = [
    {
      title: 'Empregados Ativos',
      value: dashboard?.empregados_ativos || 0,
      total: dashboard?.total_empregados || 0,
      icon: Users,
      color: 'bg-accent',
      link: '/empresa/empregados'
    },
    {
      title: 'Marcações Hoje',
      value: dashboard?.marcacoes_hoje || 0,
      icon: Clock,
      color: 'bg-blue-500',
      link: '/empresa/marcacoes'
    },
    {
      title: 'Horas Extras (Mês)',
      value: `${dashboard?.horas_extras_mes || 0}h`,
      icon: Timer,
      color: 'bg-purple-500',
      link: '/empresa/banco-horas'
    },
    {
      title: 'Banco de Horas',
      value: `${dashboard?.banco_horas_total || 0}h`,
      icon: Calendar,
      color: 'bg-teal-500',
      link: '/empresa/banco-horas'
    },
  ];

  const alerts = [
    {
      title: 'Ocorrências Pendentes',
      value: dashboard?.ocorrencias_pendentes || 0,
      icon: FileText,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      link: '/empresa/ocorrencias'
    },
    {
      title: 'Atestados Pendentes',
      value: dashboard?.atestados_pendentes || 0,
      icon: Stethoscope,
      color: 'text-info',
      bgColor: 'bg-info/10',
      link: '/empresa/atestados'
    },
    {
      title: 'Alertas de Risco',
      value: dashboard?.alertas_risco || 0,
      icon: AlertTriangle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      link: '/empresa/riscos'
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="empresa-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Dashboard</h2>
          <p className="text-muted-foreground">Bem-vindo de volta, {user?.nome}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            className="bg-accent hover:bg-accent/90"
            onClick={() => navigate('/empresa/marcacoes')}
            data-testid="registrar-ponto-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Registrar Ponto
          </Button>
        </div>
      </div>

      {/* Tabs: Resumo e Gráficos */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-xs">
          <TabsTrigger value="resumo" className="flex items-center gap-2" data-testid="tab-resumo">
            <LayoutDashboard className="w-4 h-4" />
            Resumo
          </TabsTrigger>
          <TabsTrigger value="graficos" className="flex items-center gap-2" data-testid="tab-graficos">
            <PieChart className="w-4 h-4" />
            Gráficos
          </TabsTrigger>
        </TabsList>

        {/* Tab: Resumo */}
        <TabsContent value="resumo" className="mt-6 space-y-6">
          {/* Main Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card 
                key={index} 
                className="border-slate-100 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(stat.link)}
                data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="text-2xl font-bold text-primary">{stat.value}</p>
                      {stat.total !== undefined && (
                        <p className="text-xs text-muted-foreground">de {stat.total} total</p>
                      )}
                    </div>
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Alerts Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {alerts.map((alert, index) => (
              <Card 
                key={index} 
                className="border-slate-100 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(alert.link)}
                data-testid={`alert-${alert.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${alert.bgColor} rounded-xl flex items-center justify-center`}>
                      <alert.icon className={`w-6 h-6 ${alert.color}`} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{alert.title}</p>
                      <p className="text-2xl font-bold text-primary">{alert.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-['Outfit']">Ações Rápidas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => navigate('/empresa/empregados')}
                  >
                    <Users className="w-5 h-5" />
                    <span className="text-sm">Empregados</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => navigate('/empresa/escalas-trabalho')}
                  >
                    <Calendar className="w-5 h-5" />
                    <span className="text-sm">Escalas</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => navigate('/empresa/atestados')}
                  >
                    <Stethoscope className="w-5 h-5" />
                    <span className="text-sm">Atestados</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => navigate('/empresa/cartao-ponto')}
                  >
                    <FileText className="w-5 h-5" />
                    <span className="text-sm">Cartões de Ponto</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">Resumo do Mês</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-muted-foreground">Escalas Ativas</span>
                    <span className="font-semibold">{dashboard?.escalas_ativas || 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-muted-foreground">Horas Extras Acumuladas</span>
                    <span className="font-semibold text-accent">+{dashboard?.horas_extras_mes || 0}h</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-muted-foreground">Saldo Banco de Horas</span>
                    <span className={`font-semibold ${(dashboard?.banco_horas_total || 0) >= 0 ? 'text-accent' : 'text-destructive'}`}>
                      {(dashboard?.banco_horas_total || 0) >= 0 ? '+' : ''}{dashboard?.banco_horas_total || 0}h
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">Alertas Ativos</span>
                    <span className={`font-semibold ${(dashboard?.alertas_risco || 0) > 0 ? 'text-destructive' : 'text-accent'}`}>
                      {dashboard?.alertas_risco || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Gráficos */}
        <TabsContent value="graficos" className="mt-6">
          <DashboardGraficosContent />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmpresaDashboard;
